export interface SidebarCursorPosition {
  x: number
  y: number
}

const SIDEBAR_ITEM_SELECTOR = '[data-widget-sidebar-item]'
const SIDEBAR_PANEL_SELECTOR = '[data-widget-area-panel]'

/**
 * Utility to query the sidebar panel element
 */
export function getSidebarPanelElement(): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null
  }
  return document.querySelector(SIDEBAR_PANEL_SELECTOR) as HTMLElement | null
}

/**
 * Returns the ordered list of sidebar widget DOM nodes.
 */
export function getSidebarWidgetElements(panel?: HTMLElement | null): HTMLElement[] {
  if (!panel) {
    return []
  }
  return Array.from(panel.querySelectorAll<HTMLElement>(SIDEBAR_ITEM_SELECTOR))
}

/**
 * Calculates the insertion index within the sidebar grid based on the cursor position.
 * Accounts for the two-column grid layout by considering both the vertical and horizontal
 * placement relative to each widget tile.
 */
export function determineSidebarDropIndex(options: {
  panel: HTMLElement | null
  cursor: SidebarCursorPosition | null
  totalWidgets: number
}): number {
  const { panel, cursor, totalWidgets } = options
  if (!panel || !cursor) {
    return totalWidgets
  }

  const panelRect = panel.getBoundingClientRect()
  const elements = getSidebarWidgetElements(panel)
  if (!elements.length) {
    return totalWidgets
  }

  const relativeX = cursor.x - panelRect.left
  const relativeY = cursor.y - panelRect.top

  for (let index = 0; index < elements.length; index += 1) {
    const element = elements[index]
    const rect = element.getBoundingClientRect()
    const top = rect.top - panelRect.top
    const bottom = rect.bottom - panelRect.top
    const left = rect.left - panelRect.left
    const right = rect.right - panelRect.left

    if (relativeY < top) {
      return index
    }

    const isWithinVerticalBounds = relativeY >= top && relativeY <= bottom
    if (isWithinVerticalBounds) {
      if (relativeX <= left) {
        return index
      }

      if (relativeX >= left && relativeX <= right) {
        return index
      }
    }
  }

  return Math.min(totalWidgets, elements.length)
}
